import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Wheat, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CONTACT } from "../data/content";
import { createInquiry } from "../lib/api";
import { useProducts } from "../context/ProductsContext";

export const ContactFooter = () => {
  // Contact email is editable in the admin dashboard (Settings tab).
  const email = useProducts()?.settings?.contact_email || CONTACT.email;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await createInquiry(form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-olive">Get in Touch</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight">
            Questions? Say hello
          </h2>
          <p className="mt-4 text-[#524B46] leading-relaxed max-w-md">
            Whether it's an order inquiry or a custom request, drop us a message and we'll get back within one business day.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { icon: MapPin, text: CONTACT.address },
              { icon: Phone, text: CONTACT.phone },
              { icon: Mail, text: email },
            ].map((r) => (
              <div key={r.text} className="flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#F3F7F1] text-olive shrink-0">
                  <r.icon size={18} />
                </span>
                <span className="text-[#524B46]">{r.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-2xl bg-white border border-[#EFE9E0]">
            <div className="flex items-center gap-2 text-ink font-semibold">
              <Clock size={18} className="text-olive" /> Home Studio Hours
            </div>
            <ul className="mt-3 space-y-1.5">
              {CONTACT.hours.map((h) => (
                <li key={h.day} className="flex justify-between text-sm text-[#524B46]">
                  <span>{h.day}</span><span className="font-medium text-ink">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form"
          className="bg-white rounded-3xl border border-[#EFE9E0] p-6 sm:p-8 space-y-5 h-fit shadow-[0_30px_70px_-50px_rgba(45,40,37,0.5)]">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" data-testid="contact-name" required value={form.name}
                onChange={(e) => set("name", e.target.value)} className="h-12 bg-cream" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" data-testid="contact-email" type="email" required value={form.email}
                onChange={(e) => set("email", e.target.value)} className="h-12 bg-cream" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="c-phone">Phone (optional)</Label>
            <Input id="c-phone" data-testid="contact-phone" value={form.phone}
              onChange={(e) => set("phone", e.target.value)} className="h-12 bg-cream" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="c-msg">Message</Label>
            <Textarea id="c-msg" data-testid="contact-message" required rows={5} value={form.message}
              onChange={(e) => set("message", e.target.value)} className="bg-cream resize-none"
              placeholder="Tell us how we can help..." />
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={sending}
            className="w-full h-13 rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-12">
            <Send size={16} className="mr-2" /> {sending ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </div>

      <footer className="bg-[#252220] text-[#D8D0C6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-olive text-cream">
                <Wheat size={22} />
              </span>
              <span className="font-serif text-lg text-cream">Healthy Home Bakery</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#A89F94] max-w-xs">
              Sugar-free, diabetic-friendly baked goods handcrafted with love in Tampa, FL.
            </p>
          </div>
          <div>
            <h4 className="text-cream font-semibold text-sm uppercase tracking-wider">Explore</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {["Menu", "About", "Custom Order", "FAQ"].map((l) => (
                <li key={l}>
                  <a href={`#${l.toLowerCase().replace(" ", "-")}`} className="hover:text-terracotta transition-colors">{l}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-cream font-semibold text-sm uppercase tracking-wider">Follow Us</h4>
            <div className="mt-4 flex gap-3">
              {CONTACT.socials.map((s) => (
                <a key={s.label} href={s.href} data-testid={`social-${s.label.toLowerCase()}`}
                  className="text-sm px-4 py-2 rounded-full border border-[#3D3833] hover:border-terracotta hover:text-terracotta transition-colors">
                  {s.label}
                </a>
              ))}
            </div>
            <p className="mt-5 text-sm text-[#A89F94]">{email}</p>
          </div>
        </div>
        <div className="border-t border-[#3D3833]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-[#8A8178] flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Healthy Home Bakery. All rights reserved.</span>
            <span>Sweetened with Maple Syrup · Honey · Bananas</span>
          </div>
        </div>
      </footer>
    </section>
  );
};
